import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Company } from "@somasi/shared";
import { ArrowLeft } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { CompanyAside } from "@/components/company-aside";
import { CompanyForm } from "@/components/company-form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { STATUS_LABEL, STATUS_TONE } from "@/lib/companies";

/**
 * One company, edited on a page of its own.
 *
 * It was a dialog, and what it has to hold outgrew one: the AFR history is a
 * list of financial years with a filing action on each and documents behind
 * those, and attaching a PDF meant a dialog on a dialog on a dialog. A route
 * also means a company is a link — the register, the client group page and the
 * AFR register can all point at one.
 *
 * `companies_` rather than `companies` so it is a sibling of the list rather
 * than a child: `companies.tsx` draws a full-page table and renders no
 * `<Outlet />`, so a child route would have nowhere to appear. The same reason
 * `client-groups_.$groupId` is spelled that way.
 *
 * `new` is handled here rather than as its own route, because the form is the
 * same form — only the heading and what the save does differ.
 */
/** The tabs this page can be opened on. */
const TABS = ["basic", "compliance", "billing"] as const;
type CompanyTab = (typeof TABS)[number];

export const Route = createFileRoute("/_authenticated/companies_/$companyId")({
  component: CompanyPage,
  /**
   * `?tab=compliance` opens the page on that tab.
   *
   * What the AFR register links to: somebody arriving from a financial year
   * wants the company's compliance, not its trading name, and landing on Basic
   * means a click to get to the thing they came for.
   *
   * An unknown value falls back to Basic rather than erroring — a stale link is
   * a page that opens somewhere sensible, not a broken route.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: CompanyTab } => ({
    tab: TABS.includes(search.tab as CompanyTab) ? (search.tab as CompanyTab) : undefined,
  }),
});

function CompanyPage() {
  const { companyId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = companyId === "new";

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["company", companyId],
    enabled: !isNew,
    queryFn: async () => {
      const { data, error: failure } = await api.companies.get(companyId);
      if (failure) throw new Error(failure.message);
      return data as Company;
    },
  });

  const done = (saved: Company) => {
    // The list, the pickers, and this page's own copy — the last because the
    // AFR panel below reads the company's stamped columns, and a save that
    // left them stale would show the previous state until a reload.
    qc.invalidateQueries({ queryKey: ["companies"] });
    qc.invalidateQueries({ queryKey: ["company", saved.id] });
    if (isNew) void navigate({ to: "/companies/$companyId", params: { companyId: saved.id } });
  };

  if (!isNew && isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  if (!isNew && (error || !company)) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "That company could not be found."}
        </p>
        <Button variant="outline" asChild>
          <Link to="/companies">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to companies
          </Link>
        </Button>
      </div>
    );
  }

  /*
   * A plain `<div>` that grows with its content: the shell's `<main>` stays the
   * one scroll container. `min-h-full` rather than `h-full`, so the form spans
   * the whole scroll height and its sticky action bar has room to stick in —
   * see the note on the bar in `company-form.tsx`.
   */
  return (
    <div className="flex min-h-full flex-col">
      <PageHeader
        title={
          isNew ? (
            "New company"
          ) : (
            <>
              {company?.legal_name}
              {/* Beside the name, on its baseline. It is a property of the
                  company, not an action, so it does not belong over with the
                  back button. */}
              {company && (
                <Badge className={STATUS_TONE[company.status]}>
                  {STATUS_LABEL[company.status]}
                </Badge>
              )}
            </>
          )
        }
        description={
          isNew
            ? "Fields marked * are required — fill in the rest as you go."
            : [
                company?.registration_number && `Reg. ${company.registration_number}`,
                company?.jurisdiction,
              ]
                .filter(Boolean)
                .join(" · ")
        }
        actions={
          // "Back", not "Companies": the arrow already says where it goes, and
          // the breadcrumb above names the list.
          <Button variant="outline" asChild>
            <Link to="/companies">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Link>
          </Button>
        }
      />

      {/* The tabs show one third of a company at a time, so the aside carries
          what somebody needs while editing any of them — the registry numbers,
          the client group, how far behind the AFR is. It is handed to the form
          rather than placed beside it, so it starts level with the first field
          group instead of with the tab bar. Nothing to summarise until the
          company exists. */}
      <CompanyForm
        editing={company ?? null}
        defaultTab={tab}
        aside={(live) => <CompanyAside company={live} />}
        onSaved={done}
        onCancel={() => void navigate({ to: "/companies" })}
      />
    </div>
  );
}
